import { classifySource, isMeaningfulTouch, touchesDifferForLastTouch } from './classify';
import {
  readFirstTouch,
  readLastTouch,
  readSessionTouch,
  readFirstLandingPage,
  writeFirstTouch,
  writeLastTouch,
  writeSessionTouch,
} from './storage';

const MARKETING_PARAMS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'gclid',
  'gbraid',
  'wbraid',
];

function readParamsFromUrl(search = '') {
  const params = new URLSearchParams(search || (typeof window !== 'undefined' ? window.location.search : ''));
  const out = {};
  for (const key of MARKETING_PARAMS) {
    const val = params.get(key);
    if (val) out[key] = val.slice(0, 500);
  }
  return out;
}

function buildTouchFromContext(search) {
  const urlParams = readParamsFromUrl(search);
  const referrer = typeof document !== 'undefined' ? document.referrer || '' : '';
  const landingPage =
    typeof window !== 'undefined'
      ? `${window.location.pathname}${window.location.search}`
      : '';
  const touch = {
    ...urlParams,
    referrer: referrer.slice(0, 2000),
    landing_page: landingPage.slice(0, 500),
    current_page: landingPage.slice(0, 500),
    timestamp: new Date().toISOString(),
  };
  if (typeof navigator !== 'undefined' && navigator.userAgent) {
    touch.user_agent = navigator.userAgent.slice(0, 500);
  }
  touch.source_classification = classifySource(touch);
  return touch;
}

/**
 * Parse current URL and persist first/last/session attribution.
 * Call once on app bootstrap.
 */
export function captureAttributionFromPageLoad() {
  if (typeof window === 'undefined') return null;

  const touch = buildTouchFromContext();
  const meaningful = isMeaningfulTouch(touch);

  if (meaningful || !readFirstTouch()) {
    writeFirstTouch(touch);
  }

  const prevLast = readLastTouch();
  if (!prevLast || touchesDifferForLastTouch(prevLast, touch)) {
    if (meaningful || !prevLast) {
      writeLastTouch(touch);
    }
  }

  writeSessionTouch(touch);

  if (process.env.NODE_ENV === 'development') {
    console.debug('[cognidoor:attribution] captured', {
      first: readFirstTouch(),
      last: readLastTouch(),
      session: readSessionTouch(),
    });
  }

  return touch;
}

/**
 * Full snapshot for checkout, auth sync, and analytics.
 */
export function getAttributionSnapshot() {
  const firstTouch = readFirstTouch();
  const lastTouch = readLastTouch() || firstTouch;
  const session = readSessionTouch() || lastTouch || firstTouch;
  const checkoutTouch = lastTouch || session || firstTouch;

  const flat = checkoutTouch || {};
  return {
    first_touch: firstTouch,
    last_touch: lastTouch,
    session,
    checkout: checkoutTouch,
    first_landing_page: readFirstLandingPage() || firstTouch?.landing_page || null,
    landing_page: flat.landing_page || null,
    current_page: typeof window !== 'undefined'
      ? `${window.location.pathname}${window.location.search}`
      : flat.current_page || null,
    referrer: flat.referrer || null,
    gclid: flat.gclid || null,
    gbraid: flat.gbraid || null,
    wbraid: flat.wbraid || null,
    utm_source: flat.utm_source || null,
    utm_medium: flat.utm_medium || null,
    utm_campaign: flat.utm_campaign || null,
    utm_content: flat.utm_content || null,
    utm_term: flat.utm_term || null,
    source_classification: flat.source_classification || classifySource(flat),
    captured_at: new Date().toISOString(),
  };
}

export function refreshAttribution() {
  return captureAttributionFromPageLoad();
}
